import * as React from 'react';

import {
  NumberField as AriaNumberField,
  NumberFieldProps as AriaNumberFieldProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
} from 'react-aria-components';

import { Currency, allCurrencies } from '../../..';
import { cn } from '../../../lib/stylingUtils';
import { FieldError, FieldGroup, Label } from '../Field';
import { Select } from '../pickers/Select';

import { NumberFieldInput } from './NumberField';

// Currency selector component for MoneyField
interface CurrencyComboBoxProps {
  currencies?: Currency[];
  selectedCurrency?: string;
  onCurrencyChange?: (code?: string) => void;
  isDisabled?: boolean;
  className?: string;
}

const CurrencyComboBox = ({
  currencies = allCurrencies,
  selectedCurrency,
  isDisabled,
  onCurrencyChange,
  className,
}: CurrencyComboBoxProps) => {
  const handleSelectionChange = (key: string | undefined) => {
    if (key && onCurrencyChange) {
      onCurrencyChange?.(key);
    }
  };

  const options = React.useMemo(
    () =>
      currencies.map((currency) => ({
        key: currency.code,
        label: (
          <div className="flex items-center gap-2">
            <span className="w-9 font-medium">{currency.code}</span>
            <span className="text-xs text-neutral/40">{currency.name}</span>
          </div>
        ),
        selectedLabel: currency.code,
      })),
    [currencies],
  );

  return (
    <Select
      className={cn('@CurrencyComboBox', 'w-16 !min-w-0', className)}
      value={selectedCurrency}
      onChange={handleSelectionChange}
      isDisabled={isDisabled}
      options={options}
      showArrow={true}
    />
  );
};

// Define the MoneyField props interface
export interface MoneyFieldProps extends Omit<
  AriaNumberFieldProps,
  'onChange'
> {
  /** Label text displayed above the input */
  label?: string;
  /** Description text displayed below the input */
  description?: string;
  /** Error message shown when validation fails */
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  /** The currently selected currency code */
  currencyCode: string;
  /** Callback when the currency code changes */
  onCurrencyChange?: (code?: string) => void;
  /** The current numeric amount value */
  amount: number;
  /** Callback when the amount changes */
  onAmountChange?: (value: number) => void;
  /** Whether the currency can be changed */
  currencyLocked?: boolean;
  /** List of available currencies (defaults to common currencies) */
  currencies?: Currency[];
}

/**
 * MoneyField component that combines a number input for the amount and
 * a combo box for the currency.
 */
export const MoneyField = ({
  label,
  description,
  errorMessage,
  className,
  currencyCode,
  amount,
  onCurrencyChange,
  onAmountChange,
  currencyLocked = false,
  currencies = allCurrencies,
  ...props
}: MoneyFieldProps) => {
  const handleValueChange = (value: number | null) => {
    onAmountChange?.(value ?? 0);
  };

  return (
    <AriaNumberField
      className={composeRenderProps(className, (className) =>
        cn('@MoneyField', 'group flex flex-col gap-2', className),
      )}
      value={amount}
      onChange={handleValueChange}
      {...props}
    >
      {label && <Label>{label}</Label>}
      <FieldGroup className="flex min-w-20 items-center gap-1">
        <CurrencyComboBox
          currencies={currencies}
          selectedCurrency={currencyCode}
          onCurrencyChange={onCurrencyChange}
          isDisabled={currencyLocked}
          className="w-20"
        />
        <NumberFieldInput className="pr-0 text-right" />
      </FieldGroup>
      {description && (
        <div className="text-sm text-foreground/20">{description}</div>
      )}
      <FieldError>{errorMessage}</FieldError>
    </AriaNumberField>
  );
};
